import React, { useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";

// Fire-themed lines Mochi says when she enters her brief celebratory
// "fire dance" upon reaching Inferno. Picked at random on activation.
const FIRE_LINES = [
    "I am LITERALLY made of rice and somehow I feel powerful",
    "this is my villain origin story.",
    "we are LOCKED IN. flames. drama. unstoppable.",
    "is it hot in here or is it just my new identity?",
    "feed me one more and I might ascend.",
];

// 30-second celebratory overlay that fires when Mochi's Hype Meter hits
// Inferno. Drifts ~14 flame particles upward around the avatar, dims the
// rest of the screen slightly, and replaces the speech bubble line via the
// `onLine(line)` callback. Auto-dismisses after 30s or on tap.
//
//   <FireDanceOverlay active={inferno} onDone={...} onLine={setBubbleMsg} />
export default function FireDanceOverlay({ active, onDone, onLine }) {
    // Pick a fresh fire-themed line each time the overlay activates.
    const line = useMemo(
        () => FIRE_LINES[Math.floor(Math.random() * FIRE_LINES.length)],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [active],
    );

    useEffect(() => {
        if (!active) return;
        onLine?.(line);
        const t = setTimeout(() => onDone?.(), 30_000);
        return () => clearTimeout(t);
    }, [active, line, onLine, onDone]);

    return (
        <AnimatePresence>
            {active && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0, transition: { duration: 0.6 } }}
                    transition={{ duration: 0.35 }}
                    onClick={() => onDone?.()}
                    className="fixed inset-0 z-40 pointer-events-auto"
                    data-testid="fire-dance-overlay"
                    aria-label="Mochi reached Stuffed"
                >
                    {/* Warm glow at the bottom of the viewport for depth */}
                    <div
                        className="absolute inset-0 pointer-events-none"
                        style={{
                            background:
                                "radial-gradient(ellipse at center 38%, rgba(217,108,84,0.22), transparent 55%)",
                        }}
                    />

                    {/* Flame particles — drift upward from the bottom half of the screen.
                        Positioned around the avatar's vertical center (~38vh on mobile). */}
                    {Array.from({ length: 14 }).map((_, i) => {
                        const left = 18 + (i * 67) % 64; // spread 18%..82%
                        const delay = (i * 0.27) % 2.4;
                        const dur = 2.4 + ((i * 0.31) % 1.8);
                        const size = 14 + (i % 4) * 5; // 14..29 px
                        const drift = (i % 2 === 0 ? -1 : 1) * (14 + (i % 5) * 6);
                        return (
                            <motion.span
                                key={`flame-${i}`}
                                initial={{ opacity: 0, y: 0, x: 0, scale: 0.4 }}
                                animate={{
                                    opacity: [0, 1, 1, 0],
                                    y: [-10, -240],
                                    x: [0, drift],
                                    scale: [0.4, 1.05, 0.7],
                                }}
                                transition={{
                                    duration: dur,
                                    repeat: Infinity,
                                    delay,
                                    ease: "easeOut",
                                }}
                                className="absolute pointer-events-none"
                                style={{
                                    left: `${left}%`,
                                    top: "62%",
                                    fontSize: `${size}px`,
                                    color: "#D96C54",
                                    filter: "drop-shadow(0 0 8px rgba(217,108,84,0.6))",
                                }}
                                aria-hidden
                            >
                                🔥
                            </motion.span>
                        );
                    })}

                    {/* Tiny INFERNO banner up top — non-intrusive */}
                    <motion.div
                        initial={{ y: -16, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        transition={{ delay: 0.25, type: "spring", stiffness: 200, damping: 18 }}
                        className="absolute top-4 left-1/2 -translate-x-1/2 px-4 py-1.5 rounded-full bg-terracotta text-cream font-display text-sm tracking-widest uppercase shadow-float"
                        data-testid="fire-dance-banner"
                    >
                        STUFFED · 🔥 fire dance
                    </motion.div>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
